// D-2 SC2: the clinic letterhead card (Settings). The clinic head edits it; every other active member sees it read-only.
// Reads/writes ONLY through clinic_branding_get / clinic_branding_save (definer RPCs) and the private clinic-library bucket.
// Until the SC1 migration is live the read fails: the card says so, logs a ❌ and offers nothing.
import { removableClinicLogoPaths, logoPathFor } from './guard.js';

const H = () => window.HUD || {};
const T = (k, p) => H().T?.(k, p) || k;
const el = (...a) => H().el(...a);
// "Last changed by {name}": the name is isolated in a <bdi> so a Latin name with a hyphen keeps its own direction inside Hebrew RTL text. T() returns a string, so split around a private-use marker; if the
// result is not exactly two parts (a missing key, or a translation without {name}) fall back to the plain T() string so nothing is dropped.
const _MARK = String.fromCharCode(0xE000);
function _changedBy(name) {
  const parts = T('cb_changed_by', { name: _MARK }).split(_MARK);
  if (parts.length !== 2) return [T('cb_changed_by', { name })];
  return [parts[0], el('bdi', { style: { display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere' } }, [name]), parts[1]];
}
const mkBtn = (...a) => H().mkBtn(...a);
const toast = (m, t) => H().toast?.(m, t);
const BUCKET = 'clinic-library';
const COLOURS = ['#0d9488', '#1e3a5f', '#166534', '#7f1d1d', '#0f172a'];
const TEXT = [
  ['practice_name', 'settings_brand_practice_name', 'settings_brand_practice_name_ph', 'text'],
  ['practice_address', 'profile_address', 'settings_brand_address_ph', 'text'],
  ['practice_phone', 'profile_phone', 'settings_brand_phone_ph', 'tel'],
  ['practice_email', 'settings_email', 'settings_brand_email_ph', 'email'],
];
const offerKey = id => 'hud_cb_offer_off_' + id;
const offerOff = id => { try { return localStorage.getItem(offerKey(id)) === '1'; } catch (e) { return false; } };
const setOfferOff = id => { try { localStorage.setItem(offerKey(id), '1'); } catch (e) { /* per-viewer convenience only */ } };

async function getRow(clinicId) {
  const { data, error } = await H()._supa.rpc('clinic_branding_get', { p_clinic: clinicId });
  if (error) { console.error('❌ clinic_branding_get:', error); throw error; }
  return data || null;
}
async function saveRow(clinicId, patch) {
  const { data, error } = await H()._supa.rpc('clinic_branding_save', { p_clinic: clinicId, p_patch: patch });
  if (error) { console.error('❌ clinic_branding_save:', error); throw error; }
  return data;
}
async function signedUrl(bucket, path) {
  const { data, error } = await H()._supa.storage.from(bucket).createSignedUrl(path, 120);
  if (error || !data?.signedUrl) { console.error('❌ clinic logo signed URL:', error); return null; }
  return data.signedUrl;
}

// One card for one clinic. role: 'head' | 'member'. Returns the card element; it fills itself in.
export function renderClinicBrandingCard({ clinicId, clinicName, role, userId }) {
  const isHead = role === 'head';
  const card = el('div', { class: 'cb-card', style: { marginTop: '14px', padding: '14px', background: 'var(--mint-ll)', borderRadius: 'var(--r)', border: '1px solid var(--mint-l)' } });
  const body = el('div');
  const hdr = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' } });
  hdr.appendChild(el('div', { style: { fontWeight: 800, color: 'var(--navy)', fontSize: '.86rem' } }, ['🏛️ ' + T('cb_title') + (clinicName ? ' — ' + clinicName : '')]));
  const pencil = el('button', { class: 'cb-edit', style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '4px', color: 'var(--slate)', display: 'none' } }, ['✏️']);
  hdr.appendChild(pencil);
  card.append(hdr, body);

  let row = null, state = 'loading', editing = false;
  let draft = null, logoPath = null, uploads = [], seed = null;

  const logoBox = path => {
    const box = el('div', { style: { width: '40px', height: '40px', borderRadius: '6px', background: '#fff', border: '1px solid var(--mint-l)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' } });
    if (path) signedUrl(BUCKET, path).then(u => { if (u) box.appendChild(el('img', { src: u, style: { width: '100%', height: '100%', objectFit: 'contain' } })); });
    return box;
  };

  function showDisplay() {
    body.innerHTML = '';
    pencil.style.display = isHead && state === 'ready' ? 'inline-flex' : 'none';
    if (state === 'loading') { body.appendChild(el('div', { style: { color: 'var(--slate)', fontSize: '.84rem' } }, [T('btn_loading')])); return; }
    if (state === 'error') { body.appendChild(el('div', { class: 'cb-error', style: { color: 'var(--slate)', fontSize: '.84rem' } }, [T('cb_load_failed')])); return; }
    if (!row) {
      body.appendChild(el('div', { class: 'cb-none', style: { color: 'var(--slate)', fontSize: '.84rem' } }, [T(isHead ? 'cb_none_head' : 'cb_none_member')]));
      if (isHead && !offerOff(clinicId)) {
        const o = el('div', { class: 'cb-offer', style: { display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' } });
        o.appendChild(mkBtn(T('cb_own_offer'), 'btn-sm btn-secondary', startFromOwn));
        o.appendChild(mkBtn(T('cb_own_dismiss'), 'btn-sm btn-ghost', () => { setOfferOff(clinicId); showDisplay(); }));
        body.appendChild(o);
      }
      return;
    }
    const g = el('div', { style: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '.84rem' } });
    const line = (label, val) => { g.appendChild(el('div', { style: { color: 'var(--slate)', fontWeight: 600 } }, [label + ':'])); g.appendChild(el('div', { style: { color: val ? 'var(--navy)' : 'var(--slate-l)' } }, [val || '—'])); };
    TEXT.forEach(([k, lk]) => line(T(lk), row[k]));
    g.appendChild(el('div', { style: { color: 'var(--slate)', fontWeight: 600 } }, [T('settings_brand_logo') + ':']));
    g.appendChild(row.logo_path ? logoBox(row.logo_path) : el('div', { style: { color: 'var(--slate-l)' } }, ['—']));
    line(T('settings_brand_font_style'), (row.font_style || 'sans-serif') === 'serif' ? T('settings_brand_font_opt_serif') : T('settings_brand_font_opt_sans'));
    line(T('settings_brand_hs'), (row.header_style || 'compact') === 'centered' ? T('settings_brand_hs_opt_centered') : T('settings_brand_hs_opt_compact'));
    line(T('settings_brand_footer_text'), row.footer_text);
    body.appendChild(g);
    body.appendChild(el('div', { style: { fontSize: '.7rem', color: 'var(--slate)', marginTop: '8px', fontStyle: 'italic' } }, [T(isHead ? 'cb_hint_head' : 'cb_hint_member')]));
    if (row.updated_by_name) body.appendChild(el('div', { class: 'cb-by', style: { fontSize: '.7rem', color: 'var(--slate-l)', marginTop: '2px' } }, _changedBy(row.updated_by_name)));
  }

  function showEdit() {
    body.innerHTML = '';
    pencil.style.display = 'none';
    const src = seed || row || {};
    draft = { font_style: src.font_style || 'sans-serif', header_style: src.header_style || 'compact', header_color: src.header_color || COLOURS[0] };
    logoPath = seed ? (seed._logo || null) : (row?.logo_path || null);
    const inputs = {};
    const w = el('div', { class: 'cb-form' });
    TEXT.forEach(([k, lk, pk, type]) => {
      w.appendChild(el('div', { class: 'inp-label', style: { marginTop: '6px' } }, [T(lk)]));
      inputs[k] = el('input', { class: 'inp', type, placeholder: T(pk), value: src[k] || '' });
      w.appendChild(inputs[k]);
    });
    // logo: private bucket, the clinic's own branding folder, uploaded here, saved with the row
    w.appendChild(el('div', { class: 'inp-label', style: { marginTop: '10px' } }, [T('settings_brand_logo')]));
    const lrow = el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } });
    let prev = logoBox(logoPath);
    lrow.appendChild(prev);
    const repaintLogo = () => { const n = logoBox(logoPath); prev.replaceWith(n); prev = n; };
    lrow.appendChild(mkBtn(T('settings_brand_logo_upload'), 'btn-sm btn-secondary', () => {
      const fi = el('input', { type: 'file', accept: 'image/png,image/jpeg', style: { display: 'none' } });
      fi.onchange = async ev => {
        const f = ev.target.files?.[0]; fi.remove();
        if (!f) return;
        if (f.size > 500 * 1024) { toast(T('settings_brand_logo_big'), 'error'); return; }
        const path = logoPathFor(clinicId, Date.now(), f.type);
        const { error } = await H()._supa.storage.from(BUCKET).upload(path, f, { contentType: f.type, upsert: false });
        if (error) { console.error('❌ clinic logo upload:', error); toast(T('settings_brand_logo_fail'), 'error'); return; }
        logoPath = path; uploads.push(path); repaintLogo(); toast(T('settings_brand_logo_ok'));
      };
      document.body.appendChild(fi); fi.click();
    }));
    lrow.appendChild(mkBtn(T('btn_delete'), 'btn-sm btn-ghost', () => { logoPath = null; repaintLogo(); }));   // clears on screen only; the file goes after Save confirms
    w.appendChild(lrow);
    // header colour, font, header style
    w.appendChild(el('div', { class: 'inp-label', style: { marginTop: '10px' } }, [T('rb2_colours_title')]));
    const sw = el('div', { style: { display: 'flex', gap: '8px' } });
    const paintSw = () => { sw.innerHTML = ''; COLOURS.forEach(c => { const b = el('button', { type: 'button', title: c, style: { width: '26px', height: '26px', borderRadius: '50%', background: c, cursor: 'pointer', border: draft.header_color === c ? '3px solid var(--navy)' : '2px solid #e8f4f2' } }); b.onclick = () => { draft.header_color = c; paintSw(); }; sw.appendChild(b); }); };
    paintSw(); w.appendChild(sw);
    const radios = (label, key, vals, labels) => {
      w.appendChild(el('div', { class: 'inp-label', style: { marginTop: '10px' } }, [label]));
      const r = el('div', { style: { display: 'flex', gap: '14px', flexWrap: 'wrap' } });
      vals.forEach((v, i) => {
        const id = 'cb_' + key + '_' + v;
        const inp = el('input', { type: 'radio', name: 'cb_' + key, id });
        inp.checked = draft[key] === v; inp.onchange = () => { draft[key] = v; };
        r.appendChild(el('label', { for: id, style: { display: 'flex', gap: '5px', alignItems: 'center', fontSize: '.84rem' } }, [inp, labels[i]]));
      });
      w.appendChild(r);
    };
    radios(T('settings_brand_font_style'), 'font_style', ['sans-serif', 'serif'], [T('settings_brand_font_opt_sans'), T('settings_brand_font_opt_serif')]);
    radios(T('settings_brand_hs'), 'header_style', ['compact', 'centered'], [T('settings_brand_hs_opt_compact'), T('settings_brand_hs_opt_centered')]);
    w.appendChild(el('div', { class: 'inp-label', style: { marginTop: '10px' } }, [T('settings_brand_footer_text')]));
    const ft = el('input', { class: 'inp', value: src.footer_text || '' });
    w.appendChild(ft);
    body.appendChild(w);

    const btns = el('div', { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' } });
    btns.appendChild(mkBtn(T('btn_cancel'), 'btn-sm btn-ghost', () => cancel()));
    btns.appendChild(mkBtn(T('btn_save'), 'btn-sm btn-primary', async e => {
      const b = e?.target; if (b) { b.disabled = true; b.textContent = T('rb_saving'); }
      try {
        const patch = { logo_path: logoPath, header_color: draft.header_color, font_style: draft.font_style, header_style: draft.header_style, footer_text: ft.value.trim() || null };
        TEXT.forEach(([k]) => { patch[k] = inputs[k].value.trim() || null; });
        const saved = await saveRow(clinicId, patch);
        const before = prevSaved;
        row = saved; seed = null; editing = false;
        await cleanup(saved?.logo_path || null, before);
        showDisplay(); toast('✅ ' + T('settings_spec_saved'));
      } catch (err) { toast(T('settings_spec_save_failed'), 'error'); if (b) { b.disabled = false; b.textContent = T('btn_save'); } }
    }));
    body.appendChild(btns);
  }

  // After a confirmed save: this edit's uploads and the previous logo that the row no longer holds (F-7 guard, clinic lane).
  async function cleanup(saved, before) {
    const cands = uploads.concat(before ? [before] : []); uploads = []; prevSaved = saved;
    const gone = removableClinicLogoPaths({ clinicId, saved, candidates: cands });
    if (!gone.length) return;
    const { error } = await H()._supa.storage.from(BUCKET).remove(gone);
    if (error) console.error('❌ clinic logo cleanup:', error);
  }
  let prevSaved = null;

  function cancel() {
    // files uploaded in an abandoned edit are removed (never the saved one)
    const gone = removableClinicLogoPaths({ clinicId, saved: row?.logo_path || null, candidates: uploads });
    uploads = []; seed = null; editing = false;
    if (gone.length) H()._supa.storage.from(BUCKET).remove(gone).then(({ error }) => { if (error) console.error('❌ clinic logo cleanup:', error); }, e => console.error('❌ clinic logo cleanup:', e));
    showDisplay();
  }

  // The one-time copy of the head's own letterhead (greenlight 7): prefilled into the edit form; the head confirms with Save.
  async function startFromOwn() {
    try {
      const { data, error } = await H()._supa.from('report_settings').select('practice_name,practice_address,practice_phone,practice_email,logo_storage_path,header_color,font_style,header_style,footer_text').eq('specialist_id', userId).limit(1);
      if (error) throw error;
      const own = data?.[0] || null;
      if (!own) { setOfferOff(clinicId); showDisplay(); return; }
      seed = { ...own, _logo: null };
      setOfferOff(clinicId);
      if (own.logo_storage_path) {   // copy the personal logo into the clinic folder (the clinic never points into a person's folder)
        const u = await signedUrl('specialist-storage', own.logo_storage_path);
        const r = u ? await fetch(u) : null;
        if (r && r.ok) {
          const blob = await r.blob();
          const path = logoPathFor(clinicId, Date.now(), blob.type);
          const up = await H()._supa.storage.from(BUCKET).upload(path, blob, { contentType: blob.type, upsert: false });
          if (up.error) console.error('❌ clinic logo copy:', up.error); else { seed._logo = path; uploads.push(path); }
        }
      }
      editing = true; showEdit();
    } catch (e) { console.error('❌ start from own letterhead:', e); toast(T('cb_own_copy_failed'), 'error'); }
  }

  pencil.onclick = () => { editing = true; showEdit(); };
  showDisplay();
  getRow(clinicId).then(r => { row = r; prevSaved = r?.logo_path || null; state = 'ready'; if (!editing) showDisplay(); },
    () => { state = 'error'; showDisplay(); });
  return card;
}

// CLINIC-SETTINGS-IA SC1: a head no longer edits the clinic letterhead here: it lives in Clinic settings under Clinic profile (the cockpit). One quiet line points there.
export function renderClinicBrandingPointer({ clinicName }) {
  const card = el('div', { class: 'cb-card cb-pointer', style: { marginTop: '14px', padding: '14px', background: 'var(--mint-ll)', borderRadius: 'var(--r)', border: '1px solid var(--mint-l)' } });
  card.appendChild(el('div', { style: { fontWeight: 800, color: 'var(--navy)', fontSize: '.86rem' } }, [T('cb_title') + (clinicName ? ' \u2014 ' + clinicName : '')]));
  card.appendChild(el('div', { class: 'cb-pointer-text', style: { fontSize: '.84rem', color: 'var(--slate)', marginTop: '4px' } }, [T('cs_brand_pointer', { section: T('clinic_settings_section'), area: T('cs_area_profile') })]));
  return card;
}

// All clinics this account is an active head or member of (never an admin): a MEMBER gets the read-only card, a HEAD gets the pointer to Clinic settings.
export function renderClinicBrandingCards({ clinics, userId }) {
  return (clinics || []).filter(c => c && (c.role === 'head' || c.role === 'member') && c.clinic_id)
    .map(c => c.role === 'head' ? renderClinicBrandingPointer({ clinicName: c.clinic_name })
      : renderClinicBrandingCard({ clinicId: c.clinic_id, clinicName: c.clinic_name, role: c.role, userId }));
}

export function initClinicBranding() {
  window.HUD_CLINIC_BRANDING = { renderClinicBrandingCard, renderClinicBrandingCards, renderClinicBrandingPointer, removableClinicLogoPaths };
}
